/**
 * The attendance archive, as a teacher is allowed to read it.
 *
 * The admin archive (`src/hooks/useAttendanceHistory.ts`) selects `attendance_records`
 * directly and joins the student with `students!inner`. A teacher cannot use that query, and
 * the reason is worth stating plainly because it is invisible until it bites:
 *
 *   The staff policy on `students` is `staff_has_enrollment_access()`, which requires an
 *   ACTIVE enrollment. A child who left in June is unreadable in September — so an inner
 *   join drops every record they ever had, and an outer one keeps the row with no name on
 *   it. On prod that is 235 of 2,734 rows. An archive that quietly loses a departed child
 *   is worse than no archive, because the silence looks like an answer.
 *
 * So the join happens in the database, in `staff_attendance_history` (SECURITY DEFINER),
 * which re-states the attendance policy's own scope test before returning anything. Every
 * row it hands back is a row the caller could already have selected for themselves; what it
 * adds is the name, grade and school of the student on that row, and nothing else — no
 * allergies, no photo, no payment state, no parent contact.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import {
  buildTeacherHistoryScope,
  EMPTY_HISTORY_SCOPE,
  type TeacherHistoryScope,
} from './attendanceHistoryScope';
import type { HistoryBand } from '../hooks/useAttendanceHistory';
import { REAL_SESSION_KEY, type AttendanceStatus } from '../types/attendance';
import type { AttendanceHistoryRow } from '../utils/attendanceHistory';

/**
 * Rows per request.
 *
 * Smaller than the admin table's hundred because this is read on a phone as day cards, and
 * a page is a "Load more" press rather than a page number — fifty is roughly four
 * after-school days or four weeks of one Chinese class, which is a scroll, not a wall.
 */
export const STAFF_HISTORY_PAGE_SIZE = 50;

interface TeacherScope {
  /** `null` means EVERY program; a list means those; `[]` means none. */
  program_ids: string[] | null;
  /** `null` and `[]` both mean none — a virtual class is only ever an explicit grant. */
  virtual_class_keys?: string[] | null;
}

export interface StaffHistoryFilters {
  /** Inclusive `YYYY-MM-DD` bounds, business TZ. */
  from: string;
  to: string;
  band: HistoryBand;
  /** One of the fourteen classes, e.g. `'chinese:3'`, or null for the whole subject. */
  virtualClassKey: string | null;
  status: AttendanceStatus | 'all';
  /** Matches the student's first or last name, across the whole range rather than the page. */
  search: string;
}

/** One row of the RPC's result set. */
interface HistoryRpcRow {
  id: string;
  student_id: string;
  first_name: string | null;
  last_name: string | null;
  grade: string | null;
  school: string | null;
  session_date: string;
  status: AttendanceStatus;
  virtual_class_key: string | null;
  program_name: string | null;
  program_type: string | null;
  checked_in_at: string | null;
  checked_in_by: string | null;
  checked_in_by_name: string | null;
  picked_up_at: string | null;
  picked_up_by_name: string | null;
  pickup_is_authorized: boolean | null;
  pickup_recorded_by_name: string | null;
  absence_reason: string | null;
  /** Rows matching the filters, not rows on this page. Identical on every row. */
  total_count: number;
}

export interface StaffHistoryPage {
  rows: AttendanceHistoryRow[];
  total: number;
  /** Where this page started, so the next one knows where to continue. */
  offset: number;
}

/** Exported for testing: the RPC row shape is the seam between the database and the page. */
export function toStaffHistoryRow(row: HistoryRpcRow): AttendanceHistoryRow {
  const first = row.first_name ?? '';
  const last = row.last_name ?? '';
  return {
    id: row.id,
    studentId: row.student_id,
    studentName: [first, last].filter(Boolean).join(' ') || 'Unknown student',
    studentSortName: last && first ? `${last}, ${first}` : last || first || 'Unknown student',
    studentGrade: row.grade,
    studentSchool: row.school,
    sessionDate: row.session_date,
    status: row.status,
    virtualClassKey: row.virtual_class_key ?? REAL_SESSION_KEY,
    programName: row.program_name,
    programType: row.program_type,
    checkedInAt: row.checked_in_at,
    checkedInBy: row.checked_in_by,
    checkedInByName: row.checked_in_by_name,
    pickedUpAt: row.picked_up_at,
    pickedUpByName: row.picked_up_by_name,
    pickupIsAuthorized: row.pickup_is_authorized,
    pickupRecordedByName: row.pickup_recorded_by_name,
    absenceReason: row.absence_reason,
  };
}

export const staffHistoryKeys = {
  page: (filters: StaffHistoryFilters) => ['staff-attendance-history', filters] as const,
  programTypes: (programIds: string[] | null) =>
    ['staff-history-program-types', programIds] as const,
};

/**
 * The `programs.type` values behind this teacher's `program_ids` — every type in the catalog
 * when that column is null, which is what null means there.
 *
 * Only the type is selected, and inactive programs are included on purpose: a camp that
 * finished in July is exactly what someone is asking about in September, and filtering on
 * `active` would take its band off the menu the week after it ended.
 */
export function useTeacherProgramTypes(staffUser: TeacherScope) {
  const programIds = staffUser.program_ids;

  return useQuery<string[]>({
    queryKey: staffHistoryKeys.programTypes(programIds),
    queryFn: async () => {
      // Scoped to no programs at all — the Chinese-only teacher. No query to run: `.in('id',
      // [])` is a round trip whose answer we already know.
      if (programIds !== null && programIds.length === 0) return [];

      let query = supabase.from('programs').select('type');
      if (programIds !== null) query = query.in('id', programIds);

      const { data, error } = await query;
      if (error) throw error;

      const types = new Set((data ?? []).map(row => (row as { type: string }).type));
      return [...types];
    },
    // The catalog changes a few times a term; the menu it feeds does not need to chase it.
    staleTime: 10 * 60_000,
  });
}

/** The bands and grades to offer this teacher, and whether we know yet. */
export function useTeacherHistoryScope(staffUser: TeacherScope): {
  scope: TeacherHistoryScope;
  isLoading: boolean;
} {
  const { data: programTypes, isLoading } = useTeacherProgramTypes(staffUser);

  // Until the types are in, the scope is empty rather than half-built: a menu that gains a
  // band a beat later would move the tab under a teacher's thumb.
  if (isLoading || !programTypes) return { scope: EMPTY_HISTORY_SCOPE, isLoading };

  return {
    scope: buildTeacherHistoryScope(programTypes, staffUser.virtual_class_keys),
    isLoading: false,
  };
}

/**
 * The archive for one class and one date range, a page at a time.
 *
 * Infinite rather than numbered pages, and that follows from the day cards: rows are grouped
 * by session date after the pages are concatenated, so a day straddling a page boundary is
 * one card that grows rather than two cards that disagree. Numbered pages would cut a
 * Tuesday in half and show each half as a separate Tuesday.
 */
export function useStaffAttendanceHistory(filters: StaffHistoryFilters, enabled = true) {
  return useInfiniteQuery<StaffHistoryPage>({
    queryKey: staffHistoryKeys.page(filters),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = pageParam as number;
      const { data, error } = await supabase.rpc('staff_attendance_history', {
        p_from: filters.from,
        p_to: filters.to,
        p_band: filters.band,
        p_virtual_class_key: filters.virtualClassKey,
        p_status: filters.status === 'all' ? null : filters.status,
        p_search: filters.search.trim() || null,
        p_limit: STAFF_HISTORY_PAGE_SIZE,
        p_offset: offset,
      });
      if (error) throw error;

      const rows = (data ?? []) as unknown as HistoryRpcRow[];
      return {
        rows: rows.map(toStaffHistoryRow),
        // No rows means no window function ran, and therefore no total to read off one.
        // Zero is the right answer for an empty page at offset 0 and for a page past the
        // end alike.
        total: rows[0]?.total_count ?? 0,
        offset,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.rows.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    // Days already past do not change while a teacher reads them.
    staleTime: 60_000,
    enabled: enabled && filters.from <= filters.to,
  });
}
